import { StarIcon, TrashIcon } from 'lucide-react'
import { memo, type ReactNode } from 'react'

import { Button } from '@/components/ui/button'

import type { BillingPaymentCard } from './billing.interface'

export interface BillingPaymentCardActionsProps {
  card: BillingPaymentCard
  isOnlyCard: boolean
  onSetDefault?: (card: BillingPaymentCard) => void
  onDelete?: (card: BillingPaymentCard) => void
}

const BillingPaymentCardActionsBase: React.FC<BillingPaymentCardActionsProps> = ({
  card,
  isOnlyCard,
  onSetDefault,
  onDelete,
}): ReactNode => {
  const canSetDefault = onSetDefault && !card.isDefault
  const canDelete = onDelete && !card.isDefault && !isOnlyCard
  const hasActions = canSetDefault || canDelete

  if (!hasActions) {
    return null
  }

  const handleSetDefault = (): void => {
    onSetDefault?.(card)
  }

  const handleDelete = (): void => {
    onDelete?.(card)
  }

  return (
    <div className="flex items-center gap-1">
      {canSetDefault && (
        <Button
          variant="ghost"
          size="icon"
          className="billing-payment-card-set-default size-8"
          onClick={handleSetDefault}
          title="Set as default"
        >
          <StarIcon className="size-4" />
          <span className="sr-only">Set as default</span>
        </Button>
      )}
      {canDelete && (
        <Button
          variant="ghost"
          size="icon"
          className="billing-payment-card-delete size-8 text-destructive hover:text-destructive"
          onClick={handleDelete}
          title="Remove card"
        >
          <TrashIcon className="size-4" />
          <span className="sr-only">Remove card</span>
        </Button>
      )}
    </div>
  )
}

export const BillingPaymentCardActions = memo(BillingPaymentCardActionsBase)

BillingPaymentCardActions.displayName = 'BillingPaymentCardActions'
